import { ReactNode } from "react";
import { useImpersonation } from "../../contexts/useSuperuser";

/**
 * Remounts its subtree whenever the impersonated identity changes.
 *
 * Ending (or starting) impersonation clears the React Query cache, but that
 * alone does not refresh every page: components stay mounted and keep showing
 * the previous user's data. React Query observers on an actively-mounted query
 * are not reliably forced to refetch by removeQueries, and pages that hold data
 * in plain local state (e.g. the AskVivo history panel) are never touched by a
 * cache wipe at all.
 *
 * Keying the subtree on the current identity forces a full remount on any
 * impersonation change, which resets all local component state and causes data
 * fetching hooks to re-run against the now-current user's APIs.
 */
export default function ImpersonationBoundary({
  children,
}: {
  children: ReactNode;
}) {
  const { impersonatedUser, impersonatedRole } = useImpersonation();

  // Remount on either a user-identity change or a role-impersonation change so
  // permission-gated UI and local component state re-derive against the now
  // current grants.
  return (
    <div
      key={`${impersonatedUser?.id ?? "self"}:${impersonatedRole ?? "none"}`}
    >
      {children}
    </div>
  );
}
